import { RealtimeConnection } from "./connection.js";
import { getScribeMicrophoneSetup } from "./microphone.js";

export enum AudioFormat {
  PCM_8000 = "pcm_8000",
  PCM_16000 = "pcm_16000",
  PCM_22050 = "pcm_22050",
  PCM_24000 = "pcm_24000",
  PCM_44100 = "pcm_44100",
  PCM_48000 = "pcm_48000",
  ULAW_8000 = "ulaw_8000",
}

export enum CommitStrategy {
  MANUAL = "manual",
  VAD = "vad",
}

/**
 * Entity categories accepted by {@link BaseOptions.entityDetection}.
 * `"all"` enables every supported entity type.
 */
export type EntityDetectionCategory =
  | "all"
  | "pii"
  | "phi"
  | "pci"
  | "other"
  | "offensive_language";

/**
 * A value accepted by {@link BaseOptions.entityDetection}: either one of the
 * {@link EntityDetectionCategory} values, or a specific entity type such as
 * `"email_address"` or `"credit_card"`.
 */
// The `string & {}` union member keeps autocomplete for the known categories
// while still accepting any specific entity type the API supports.
export type EntityDetectionOption = EntityDetectionCategory | (string & {});

interface BaseOptions {
  /**
   * Token to use for the WebSocket connection. Obtained from the ElevenLabs API.
   */
  token: string;
  /**
   * Strategy for committing transcriptions.
   * @default CommitStrategy.MANUAL
   */
  commitStrategy?: CommitStrategy;
  /**
   * Silence threshold in seconds for VAD (Voice Activity Detection).
   * Must be a positive number between 0.3 and 3.0
   */
  vadSilenceThresholdSecs?: number;
  /**
   * Threshold for voice activity detection.
   * Must be between 0.1 and 0.9.
   */
  vadThreshold?: number;
  /**
   * Minimum speech duration in milliseconds.
   * Must be a positive integer between 50 and 2000.
   */
  minSpeechDurationMs?: number;
  /**
   * Minimum silence duration in milliseconds.
   * Must be a positive integer between 50 and 2000.
   */
  minSilenceDurationMs?: number;
  /**
   * Model ID to use for transcription.
   * Must be a valid model ID.
   */
  modelId: string;
  /**
   * An ISO-639-1 or ISO-639-3 language_code corresponding to the language of the audio file.
   * Can sometimes improve transcription performance if known beforehand.
   */
  languageCode?: string;
  /**
   * Additional ISO-639-1 or ISO-639-3 language codes that may be present in the audio.
   * Providing them makes language identification more reliable by only focusing on a
   * certain set of languages.
   */
  secondaryLanguages?: string[];
  /**
   * Base URI to use for the WebSocket connection.
   * If not provided, the default URI will be used.
   */
  baseUri?: string;
  /**
   * Whether to receive a committed_transcript_with_timestamps event which includes word-level timestamps.
   * @default false
   */
  includeTimestamps?: boolean;
  /**
   * Whether to include detected language information in the transcription results.
   * @default false
   */
  includeLanguageDetection?: boolean;
  /**
   * List of keyterms to bias the model towards.
   * Maximum 50 keyterms, each up to 20 characters.
   */
  keyterms?: string[];
  /**
   * If true, removes filler words, false starts and disfluencies from the transcript.
   * @default false
   */
  noVerbatim?: boolean;
  /**
   * Detect entities on committed transcripts. Accepts `"all"`, a single entity type or
   * category, or a list of types/categories. Detected entities are delivered in a separate
   * committed_transcript_entities event with their text, type, and character positions.
   */
  entityDetection?: EntityDetectionOption | EntityDetectionOption[];
  /**
   * Natural-language instruction applied to each committed transcript (max 2000 characters).
   * The edited text is delivered in a separate edited_transcript event containing the
   * committed text and the edited text.
   *
   * @remarks
   * Cannot be combined with entityDetection. Adds a 30% premium to the base transcription
   * cost, billed for at least 10 seconds of audio per committed transcript.
   */
  transcriptEdit?: string;
  /**
   * Enable background speech filtering to reduce false activations from nearby conversations
   * and ambient noise. When enabled without an explicit vadThreshold, the server applies a
   * lower default threshold.
   *
   * @remarks
   * Cannot be combined with includeTimestamps.
   * @default false
   */
  filterBackgroundAudio?: boolean;
  /**
   * Whether the request may be logged by ElevenLabs.
   * When set to false, zero retention mode is used for the session, which means
   * history features are unavailable for it. Zero retention mode may only be
   * used by enterprise customers.
   * @default true
   */
  enableLogging?: boolean;
}

export interface AudioOptions extends BaseOptions {
  audioFormat: AudioFormat;
  sampleRate: number;
  microphone?: never;
}

/**
 * Options for automatic microphone streaming in the browser.
 */
export interface MicrophoneOptions extends BaseOptions {
  microphone?: {
    deviceId?: MediaDeviceConstraint;
    echoCancellation?: boolean;
    noiseSuppression?: boolean;
    autoGainControl?: boolean;
    channelCount?: number;
    /**
     * Allows self-hosting the Scribe audio worklet to avoid whitelisting
     * blob: and data: URLs in the CSP script-src (or script-src-elem)
     * directive. Point this at a same-origin copy of the processor shipped
     * at `@elevenlabs/client/worklets/scribeAudioProcessor.js`, e.g.
     * `{ scribeAudioProcessor: "/vendor/elevenlabs/scribe-audio-processor.js" }`.
     */
    workletPaths?: {
      scribeAudioProcessor?: string;
    };
  };
  audioFormat?: never;
  sampleRate?: never;
}

/**
 * Real-time speech-to-text transcription client for browser environments.
 * Supports microphone streaming and manual audio chunk transmission.
 */

// biome-ignore lint/complexity/noStaticOnlyClass: This class is static only because it is a singleton
export class ScribeRealtime {
  private static readonly DEFAULT_BASE_URI = "wss://api.elevenlabs.io";

  private static getWebSocketUri(
    baseUri: string = ScribeRealtime.DEFAULT_BASE_URI
  ): string {
    return `${baseUri}/v1/speech-to-text/realtime`;
  }

  private static buildWebSocketUri(
    options: AudioOptions | MicrophoneOptions
  ): string {
    const baseUri = ScribeRealtime.getWebSocketUri(options.baseUri);
    const params = new URLSearchParams();

    // Model ID and token are required, so no check required
    params.append("model_id", options.modelId);
    params.append("token", options.token);

    // Add optional parameters if provided, with validation
    if (options.commitStrategy !== undefined) {
      params.append("commit_strategy", options.commitStrategy);
    }
    if (options.audioFormat !== undefined) {
      params.append("audio_format", options.audioFormat);
    }
    if (options.vadSilenceThresholdSecs !== undefined) {
      if (
        options.vadSilenceThresholdSecs < 0.3 ||
        options.vadSilenceThresholdSecs > 3.0
      ) {
        throw new Error("vadSilenceThresholdSecs must be between 0.3 and 3.0");
      }
      params.append(
        "vad_silence_threshold_secs",
        options.vadSilenceThresholdSecs.toString()
      );
    }
    if (options.vadThreshold !== undefined) {
      if (options.vadThreshold < 0.1 || options.vadThreshold > 0.9) {
        throw new Error("vadThreshold must be between 0.1 and 0.9");
      }
      params.append("vad_threshold", options.vadThreshold.toString());
    }
    if (options.minSpeechDurationMs !== undefined) {
      if (
        options.minSpeechDurationMs < 50 ||
        options.minSpeechDurationMs > 2000
      ) {
        throw new Error("minSpeechDurationMs must be between 50 and 2000");
      }
      params.append(
        "min_speech_duration_ms",
        options.minSpeechDurationMs.toString()
      );
    }
    if (options.minSilenceDurationMs !== undefined) {
      if (
        options.minSilenceDurationMs < 50 ||
        options.minSilenceDurationMs > 2000
      ) {
        throw new Error("minSilenceDurationMs must be between 50 and 2000");
      }
      params.append(
        "min_silence_duration_ms",
        options.minSilenceDurationMs.toString()
      );
    }
    if (options.languageCode !== undefined) {
      params.append("language_code", options.languageCode);
    }
    if (options.secondaryLanguages !== undefined) {
      for (const language of options.secondaryLanguages) {
        params.append("secondary_languages", language);
      }
    }
    if (options.includeTimestamps !== undefined) {
      params.append(
        "include_timestamps",
        options.includeTimestamps ? "true" : "false"
      );
    }
    if (options.includeLanguageDetection !== undefined) {
      params.append(
        "include_language_detection",
        options.includeLanguageDetection ? "true" : "false"
      );
    }
    if (options.keyterms !== undefined) {
      for (const term of options.keyterms) {
        params.append("keyterms", term);
      }
    }
    if (options.noVerbatim !== undefined) {
      params.append("no_verbatim", options.noVerbatim ? "true" : "false");
    }
    if (options.entityDetection !== undefined) {
      const entityDetection = Array.isArray(options.entityDetection)
        ? options.entityDetection
        : [options.entityDetection];
      for (const entity of entityDetection) {
        params.append("entity_detection", entity);
      }
    }
    if (options.transcriptEdit !== undefined) {
      params.append("transcript_edit", options.transcriptEdit);
    }
    if (options.filterBackgroundAudio !== undefined) {
      params.append(
        "filter_background_audio",
        options.filterBackgroundAudio ? "true" : "false"
      );
    }
    if (options.enableLogging !== undefined) {
      params.append("enable_logging", options.enableLogging ? "true" : "false");
    }

    const queryString = params.toString();
    return queryString ? `${baseUri}?${queryString}` : baseUri;
  }

  /**
   * Establishes a WebSocket connection for real-time speech-to-text transcription.
   *
   * @param options - Configuration options for the connection
   * @returns A RealtimeConnection instance
   *
   * @example
   * ```typescript
   * // Manual audio streaming
   * const connection = Scribe.connect({
   *     token: "...",
   *     modelId: "scribe_v2_realtime",
   *     audioFormat: AudioFormat.PCM_16000,
   *     sampleRate: 16000,
   * });
   *
   * // Automatic microphone streaming
   * const connection = Scribe.connect({
   *     token: "...",
   *     modelId: "scribe_v2_realtime",
   *     microphone: {
   *         echoCancellation: true,
   *         noiseSuppression: true
   *     }
   * });
   * ```
   */
  public static connect(
    options: AudioOptions | MicrophoneOptions
  ): RealtimeConnection {
    if (!options.modelId) {
      throw new Error("modelId is required");
    }

    // Create connection object first so users can attach event listeners before messages arrive
    const sampleRate =
      "microphone" in options && options.microphone
        ? 16000
        : (options as AudioOptions).sampleRate;
    const connection = new RealtimeConnection(sampleRate);

    // Build WebSocket URI with query parameters
    const uri = ScribeRealtime.buildWebSocketUri(options);

    const websocket = new WebSocket(uri);

    // If microphone mode, set up streaming handler
    if ("microphone" in options && options.microphone) {
      websocket.addEventListener("open", () => {
        ScribeRealtime.streamFromMicrophone(
          options as MicrophoneOptions,
          connection
        );
      });
    }

    connection.setWebSocket(websocket);

    return connection;
  }

  private static async streamFromMicrophone(
    options: MicrophoneOptions,
    connection: RealtimeConnection
  ): Promise<void> {
    try {
      const setup = getScribeMicrophoneSetup();
      const result = await setup(options.microphone ?? {}, base64Audio => {
        // A frame can arrive after close(); send() throws on a closed socket.
        if (connection._closed) return;
        connection.send({ audioBase64: base64Audio });
      });

      connection._mediaStreamTrack = result.mediaStreamTrack;
      connection._audioCleanup = result.cleanup;

      // close() may have run while this setup was in flight, before
      // _audioCleanup was assigned; release the mic now so it does not leak.
      if (connection._closed) {
        result.cleanup();
        connection._audioCleanup = undefined;
      }
    } catch (error) {
      console.error("Failed to start microphone streaming:", error);
      connection._emitError(error);

      // A microphone-mode session that never acquired a microphone can never
      // send audio, so the socket is stranded: it holds a session open that
      // will only ever be silent. Close it so the failure ends the connection
      // the way any other terminal one does, and consumers tracking CLOSE can
      // start a fresh session instead of waiting on this one.
      connection.close();
    }
  }
}
